import { Injectable } from '@angular/core';
import { CameraResultType, CameraSource } from '@capacitor/camera';
import { PhotoPickerResult, PhotoPickerStrategy } from '../photo-picker.types';
import { PlatformDetectorService } from '../platform-detector.service';
import { CameraService } from '../camera.service';
import { FilesystemService } from '../filesystem.service';

/**
 * Photo picker strategy for native platforms (iOS/Android).
 * Uses CameraResultType.Uri and Filesystem.readFile() for better performance.
 */
@Injectable({
    providedIn: 'root'
})
export class NativePhotoPickerStrategy implements PhotoPickerStrategy {
    private static readonly USER_CANCEL_MESSAGES = [
        'No image picked',
        'User cancelled photos app'
    ];

    constructor(
        private readonly platformDetector: PlatformDetectorService,
        private readonly cameraService: CameraService,
        private readonly filesystemService: FilesystemService
    ) {}

    isAvailable(): boolean {
        return this.platformDetector.isNativePlatform();
    }

    async pickPhoto(source: CameraSource): Promise<PhotoPickerResult | null> {
        try {
            // quality:75 is the sweet spot for JPEG — high enough to avoid
            // visible blocking on inspection evidence photos, low enough
            // that file sizes stay in the 200-500KB range from a phone.
            // Both width AND height are set so portrait-orientation photos
            // (where width is the shorter axis) are still scaled —
            // omitting height meant a 1080×1920 portrait phone capture
            // bypassed the scaler entirely.
            const image = await this.cameraService.getPhoto({
                quality: 75,
                allowEditing: false,
                resultType: CameraResultType.Uri,
                saveToGallery: false,
                source,
                width: 1920,
                height: 1920
            });

            if (!image.path) {
                throw new Error('Unable to retrieve the path of the image');
            }
            if (!image.webPath) {
                throw new Error('Unable to retrieve the webPath of the image');
            }

            const imageFile = await this.filesystemService.readFile({ path: image.path });
            const fileName = this.extractFileName(image.webPath);
            const base64Data = await this.convertToBase64String(imageFile.data);

            return {
                fileName,
                base64Data
            };
        } catch (error) {
            if (this.isUserCancellation(error)) {
                return null;
            }
            throw error;
        }
    }

    private isUserCancellation(error: unknown): boolean {
        if (error instanceof Error) {
            return NativePhotoPickerStrategy.USER_CANCEL_MESSAGES.includes(error.message);
        }
        return false;
    }

    private extractFileName(webPath: string): string {
        return webPath.substring(webPath.lastIndexOf('/') + 1);
    }

    private async convertToBase64String(data: string | Blob): Promise<string> {
        if (typeof data === 'string') {
            return data;
        }

        // Convert Blob to base64 string
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const result = reader.result as string;
                // Remove data URL prefix if present (e.g., "data:image/png;base64,")
                const base64 = result.includes(',') ? result.split(',')[1] : result;
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(data);
        });
    }
}
